import React, { forwardRef, useEffect, useContext, useState, createRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import PropTypes from 'prop-types';
import { Formik, Field } from 'formik';
import { BastionContext } from 'context';
import { AuthLayout } from 'components/UI';
import { AuthActions } from 'state/modules/auth';
import { Box, Input, Button, Typography } from '@mui/material';

const InputRef = forwardRef((props, ref) => {
  return (
    <Field name={props.name} type="number">
      {({ field, form }) => (
        <Input
          data-cy={props.name + '-input'}
          error={props.error ? true : false}
          inputRef={ref}
          type="number"
          {...field}
          onChange={(e) => {
            e.preventDefault();
            if (e.target.value.length > 1) {
              return;
            }
            form.setFieldValue(props.name, e.target.value);
            if (props.index && e.target.value && !isNaN(e.target.value)) {
              props.refs[props.index].current.focus();
            }
          }}
          inputProps={{ ['data-cy']: props.name }}
          sx={{
            margin: '0 5px',
            '& input': {
              textAlign: 'center',
              '&::-webkit-outer-spin-button': {
                WebkitAppearance: 'none',
              },
              '&::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
              },
            },
          }}
        />
      )}
    </Field>
  );
});

const MFASchema = Yup.object().shape({
  code1: Yup.number().required(),
  code2: Yup.number().required(),
  code3: Yup.number().required(),
  code4: Yup.number().required(),
  code5: Yup.number().required(),
  code6: Yup.number().required(),
});

const MFAPage = () => {
  const history = useHistory();
  const [refs, setRefs] = useState([]);

  const dispatch = useDispatch();
  const { verifyOTP, loading, isAuthenticated, errorResendOtp } = useSelector(
    (state) => state.auth
  );

  const { theme } = useContext(BastionContext);

  const initialValues = {
    code1: '',
    code2: '',
    code3: '',
    code4: '',
    code5: '',
    code6: '',
  };

  useEffect(() => {
    setRefs([...Array(6)].map(() => createRef()));
  }, []);

  useEffect(() => {
    if (!loading && errorResendOtp?.key === 'UNAUTHENTICATED') {
      dispatch(AuthActions.resetOTP());
      history.push('/login');
    }
  }, [loading, errorResendOtp?.key, history, dispatch]);

  useEffect(() => {
    if (verifyOTP && isAuthenticated) {
      const org = sessionStorage.getItem('org_slug');
      history.push(buildUrl('/dashboard', org));
    }
  }, [verifyOTP, history, isAuthenticated]);

  return (
    <AuthLayout title={`${theme.page.title} | ${i18n.t('auth.otp')}`}>
      <Box style={{ paddingTop: '25%' }} height="100%">
        <Formik
          initialValues={initialValues}
          validationSchema={MFASchema}
          onSubmit={async (values) => {
            const code = `${values.code1}${values.code2}${values.code3}${values.code4}${values.code5}${values.code6}`;
            await dispatch(AuthActions.verifyOTP(code));
          }}
        >
          {({ handleSubmit, errors, touched, ...props }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Box mb={4}>
                <Typography component="div">
                  <Box fontSize={16} lineHeight={1} color="primary.main" fontWeight="bold">
                    {i18n.t('auth.welcome_to', { name: '' })}
                  </Box>
                  <Box mt={2} fontSize={32} lineHeight={1} fontWeight="bold">
                    {theme.page.title}
                  </Box>
                  <Box mt={1} fontSize={24} lineHeight={1} letterSpacing={6.6}>
                    {theme.page.secondTitle}
                  </Box>
                </Typography>
              </Box>
              <Typography component="div">
                <Box fontSize={16} fontWeight="bold" mb={4}>
                  {i18n.t('auth.otp_text')}
                </Box>
              </Typography>
              <Box>
                <Typography mt={6} component="div">
                  <Box fontSize={18} fontWeight="bold" mb={8}>
                    {i18n.t('auth.enter_code')}
                  </Box>
                </Typography>
              </Box>
              <Box mt={8}>
                <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                  {refs.map((_, index) => {
                    const name = `code${index + 1}`;
                    return (
                      <InputRef
                        refs={refs}
                        index={index !== refs.length - 1 ? index + 1 : null}
                        error={errors[name] && touched[name]}
                        ref={refs[index]}
                        name={name}
                        key={index}
                        {...props}
                      />
                    );
                  })}
                </Box>
              </Box>
              <Box mt={8}>
                <Button
                  fullWidth
                  type="submit"
                  color="primary"
                  loading={loading}
                  disabled={loading}
                  variant="contained"
                  sx={{ textTransform: 'capitalize' }}
                >
                  {i18n.t('common.verify')}
                </Button>
              </Box>
              <Box mt={2} display="flex" alignItems="center" justifyContent="center">
                <Typography component="div">
                  <Box mb={2} fontSize={13} component="span">
                    {i18n.t('auth.otp_resend_text')}
                  </Box>
                  <Button
                    mb={2}
                    fontSize={12}
                    color="primary"
                    component="span"
                    loading={loading}
                    disabled={loading}
                    sx={{ textTransform: 'capitalize' }}
                    onClick={() => dispatch(AuthActions.resendOTP())}
                  >
                    {i18n.t('auth.resend_code')}
                  </Button>
                </Typography>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </AuthLayout>
  );
};

InputRef.displayName = 'InputRef';

InputRef.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number,
  error: PropTypes.any,
  refs: PropTypes.any,
};

export default MFAPage;
